import type { ReactNode } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMeasure } from 'react-use';
import { cn } from '@components/lib/utils';
import { Show } from '@components/Show';
import type { DragEndEvent } from '@dnd-kit/core';
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { deriveActiveCount, QueryBuilderController } from '@features/query-builder';
import { useQueryBuilderStore } from '@features/query-builder/store';
import { ActionsBar } from '@pages/User/components/ActionsBar';
import { FiltersBar } from '@pages/User/components/FiltersBar';
import type { Action } from '@root/@types/types';
import type { ColumnFilter } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';

import { TableLoading } from '../../shared/components/TableLoading';
import { TABLE_CONSTANTS } from '../../shared/constants';
import { type UseDataTableReturn } from '../hooks/useDataTable';
import { useFiltersPanelVisibility } from '../hooks/useFiltersPanelVisibility';
import { useTableStateHandlers } from '../hooks/useTableStateHandlers';
import { TableEmptyFilterState } from '../TableEmptyFilterState';
import { TableEmptyState } from '../TableEmptyState';

import { TableHeader } from './table-header';
import { TableBody } from './TableBody';
import { TableFooter } from './TableFooter';

export interface TableContainerProps {
  actions: Action[];
  className?: string;
  enableColumnFilters: boolean;
  enableColumnVisibility: boolean;
  enableGlobalSearch: boolean;
  entityName: string;
  filtersBarWrapperClassName?: string;
  isEmpty?: boolean;
  isLoading: boolean;
  useQueryBuilder?: boolean;
  queryBuilderHeaderSlot?: ReactNode;
  queryBuilderFooterSlot?: ReactNode;
  renderCustomElements?: (
    isGroupByParent: boolean,
    setIsGroupByParent: (isGroupByParent: boolean) => void,
  ) => () => ReactNode;
  tableApi: UseDataTableReturn;
  totalResults?: number;
  handleSearchChange: (searchTerm: string) => void;
  searchTerm: string;
  fetchMoreOnBottomReached: (containerRefElement?: HTMLDivElement | null) => void;
  isFetching: boolean;
  pageSize?: number;
}

export function TableContainer({ isLoading, ...props }: TableContainerProps) {
  'use memo';

  if (isLoading) {
    return <TableLoading />;
  }

  return <LoadedTableContainer {...props} isLoading={isLoading} />;
}

function LoadedTableContainer({
  actions,
  className,
  enableColumnFilters,
  enableColumnVisibility,
  enableGlobalSearch,
  entityName,
  isEmpty,
  isLoading = false,
  useQueryBuilder = false,
  queryBuilderHeaderSlot,
  queryBuilderFooterSlot,
  renderCustomElements,
  tableApi,
  totalResults,
  handleSearchChange,
  searchTerm,
  fetchMoreOnBottomReached,
  isFetching,
  pageSize,
}: TableContainerProps) {
  'use memo';

  const { table, tableState, handlers, meta, initialState, updateState } = tableApi;

  const [isColumnVisibilityControlsVisible, setIsColumnVisibilityControlsVisible] = useState(false);
  const { isVisible: isFiltersBarVisible, toggle: toggleFiltersPanel } = useFiltersPanelVisibility(
    entityName,
    useQueryBuilder,
  );
  const [hasFiltersBarMounted, setHasFiltersBarMounted] = useState(false);
  const toggleFiltersBarVisibility = () => {
    setHasFiltersBarMounted(true);
    toggleFiltersPanel();
  };
  const [filterRef] = useMeasure<HTMLDivElement>();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  const { rows } = table.getRowModel();

  // This restores the scroll position when the table is re-rendered (on navigation)
  const restoreInitialOffset = (() => {
    const topIndex = tableState?.topIndex ?? 0;
    if (topIndex <= 0) return 0;
    return tableState?.measurementsCache?.[topIndex]?.start ?? 0;
  })();

  const rowVirtualizer = useVirtualizer({
    initialOffset: restoreInitialOffset,
    initialMeasurementsCache: tableState?.measurementsCache,
    count: rows.length,
    estimateSize: () => 33, //estimate row height for accurate scrollbar dragging
    getScrollElement: () => tableContainerRef.current,
    //measure dynamic row height, except in firefox because it measures table border height incorrectly
    measureElement:
      typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => {
            return element?.getBoundingClientRect().height;
          }
        : undefined,
    overscan: 5,
  });

  const lastTopIndexRef = useRef<number>(tableState?.topIndex ?? 0);
  const updateStateRef = useRef(updateState);

  // This updates the scroll position when the table is scrolled
  useEffect(() => {
    updateStateRef.current = updateState;
    const startIndex = rowVirtualizer.range?.startIndex;
    if (typeof startIndex === 'number' && startIndex >= 0) {
      lastTopIndexRef.current = startIndex;
    }
  }, [rowVirtualizer.range?.startIndex, updateState]);

  useEffect(() => {
    return () => {
      // Skip StrictMode effect replays that never observed a real scroll
      // offset. Otherwise an empty cache can overwrite the saved table state.
      if (rowVirtualizer.scrollOffset === null) return;
      updateStateRef.current({
        topIndex: lastTopIndexRef.current,
        measurementsCache: rowVirtualizer.measurementsCache ?? [],
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialStoredTopIndexRef = useRef(tableState?.topIndex ?? 0);
  const hasRestoredScrollRef = useRef(false);
  const restoreAttemptsRef = useRef(0);

  useLayoutEffect(() => {
    if (hasRestoredScrollRef.current) return;
    if (!meta.isInitialized || isLoading) return;
    if (rows.length === 0) return;
    const target = initialStoredTopIndexRef.current;
    if (target <= 0) {
      hasRestoredScrollRef.current = true;
      return;
    }

    restoreAttemptsRef.current++;
    const frameId = window.requestAnimationFrame(() => {
      rowVirtualizer.scrollToIndex(target, { align: 'start' });
      // scrollToIndex clamps to count-1 when target > count-1. If we have enough
      // rows the virtualizer lands on the right row; otherwise it scrolls near
      // the bottom edge of currently-loaded data, which triggers fetchNextPage
      // via the onScroll handler. The effect re-fires on rows.length / isFetching
      // changes until we've actually loaded the target row.
      if (rows.length > target || restoreAttemptsRef.current >= 30) {
        hasRestoredScrollRef.current = true;
      }
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [meta.isInitialized, isLoading, rows.length, isFetching, rowVirtualizer]);

  const stateHandlers = useTableStateHandlers({
    tableState,
    initialState,
    updateState,
    table,
    rowVirtualizer,
  });

  table.setOptions((prev) => ({
    ...prev,
    ...stateHandlers,
  }));

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleGlobalSearchClick = (searchTerm: string) => {
    rowVirtualizer.scrollToIndex(0);
    handleSearchChange(searchTerm);
  };
  const toggleColumnVisibilityControls = () => setIsColumnVisibilityControlsVisible(!isColumnVisibilityControlsVisible);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id && over?.id) {
      handlers.handleColumnOrderChange(String(active.id), String(over.id));
    }
  };

  // Query builder mode: count from store. Legacy mode: count from TanStack columnFilters.
  const qbFilterRows = useQueryBuilderStore((s) => (useQueryBuilder ? (s.rowsByEntity[entityName] ?? []) : []));
  const activeFiltersCount = useQueryBuilder
    ? deriveActiveCount(qbFilterRows)
    : table
        .getState()
        .columnFilters.filter((filter: ColumnFilter) =>
          Array.isArray(filter.value) ? filter.value.length > 0 : filter.value,
        ).length;

  const handleClearAllFiltersClick = () => {
    if (searchTerm) {
      handleGlobalSearchClick('');
    }
    if (useQueryBuilder) {
      useQueryBuilderStore.getState().clearRows(entityName);
    } else {
      const clearedFilters =
        tableState?.columnFilters?.reduce((acc: ColumnFilter[], f: ColumnFilter) => {
          if (!Array.isArray(f.value)) acc.push({ ...f, value: false });
          return acc;
        }, []) ?? [];
      table.setColumnFilters(clearedFilters);
    }
  };

  const getEmptyComponent = (activeFiltersCount: number, searchTerm: string) => {
    if (activeFiltersCount > 0 || searchTerm) {
      return <TableEmptyFilterState entityName={entityName} onClearFilters={handleClearAllFiltersClick} />;
    }
    return <TableEmptyState entityName={entityName} action={meta.createEntityAction} />;
  };

  return (
    <div className={clsx('flex max-w-full flex-col px-4 sm:px-6 lg:px-8', className)}>
      <ActionsBar
        actions={actions}
        activeFiltersCount={activeFiltersCount}
        enableColumnFilters={enableColumnFilters}
        entityName={entityName}
        handleGlobalSearchClick={enableGlobalSearch ? handleGlobalSearchClick : undefined}
        searchTerm={searchTerm}
        isColumnVisibilityControlsVisible={enableColumnVisibility ? isColumnVisibilityControlsVisible : false}
        isFiltersBarVisible={isFiltersBarVisible}
        renderCustomElements={renderCustomElements?.(tableState?.isGroupByParent ?? false, handlers.setIsGroupByParent)}
        table={table}
        toggleColumnVisibilityControls={enableColumnVisibility ? toggleColumnVisibilityControls : undefined}
        toggleFiltersBarVisibility={enableColumnFilters ? toggleFiltersBarVisibility : undefined}
      />

      <Show when={isFiltersBarVisible && enableColumnFilters && useQueryBuilder}>
        <QueryBuilderController
          entityName={entityName}
          headerSlot={queryBuilderHeaderSlot}
          footerSlot={queryBuilderFooterSlot}
        />
      </Show>
      <Show when={hasFiltersBarMounted && enableColumnFilters && !useQueryBuilder}>
        <div ref={filterRef} className={cn(!isFiltersBarVisible && 'hidden')}>
          <FiltersBar
            entityName={entityName}
            table={table}
            onClearAllFilters={handleClearAllFiltersClick}
            onHide={toggleFiltersBarVisibility}
          />
        </div>
      </Show>

      <DndContext
        autoScroll={{ threshold: { x: 0.2, y: 0 } }} // When dragging an item the y-scroll position should be frozen
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div
          className={'border-info-200 relative mt-8 flex flex-col overflow-x-hidden rounded-lg border'}
          style={{
            width: `calc(100vw - ${TABLE_CONSTANTS.DEFAULT_SIDEBAR_WIDTH}px)`,
            height: 'calc(100vh - 100px)',
            boxSizing: 'border-box',
          }}
        >
          <div
            className="relative h-full min-w-0 overflow-auto"
            onScroll={(e) => {
              fetchMoreOnBottomReached(e.currentTarget);
            }}
            ref={tableContainerRef}
          >
            <div className={clsx('inline-block h-full min-w-full align-middle', isEmpty && 'w-full overflow-hidden')}>
              <div className="flex flex-col shadow-sm">
                <table data-testid="data-table" className="grid w-full">
                  <TableHeader tableHeaderGroups={table.getHeaderGroups()} />

                  <Show when={isEmpty}>
                    <tbody>
                      <tr>
                        <td colSpan={meta.columnsCount}>{getEmptyComponent(activeFiltersCount, searchTerm)}</td>
                      </tr>
                    </tbody>
                  </Show>

                  <Show when={!isEmpty}>
                    <TableBody rows={rows} rowVirtualizer={rowVirtualizer} />
                  </Show>
                </table>
              </div>
            </div>
          </div>

          <TableFooter
            totalResults={totalResults ?? 0}
            dataLength={rows.length}
            isFetching={isFetching}
            rowVirtualizer={rowVirtualizer}
            pageSize={pageSize}
          />
        </div>
      </DndContext>
    </div>
  );
}
